import { Component } from '@angular/core';
import { OnInit, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Location } from '@angular/common';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { Subscription } from 'rxjs';
import { ViewportService } from '../../services/viewport.service';
import { UserService } from '../../services/user.service';
import { SearchService } from '../../search/services/search.service';
import { ConfigService } from '../../services/config.service';
import { AlertsService } from '../../services/alerts.service';
import { CartService } from '../../cart/services/cart.service';
import { MessagesService } from '../../services/messages.service';
import { ThemeBuilderService } from '../../theme/services/theme-builder.service';
import { IdleService } from '../../idle/services/idle.service';
import { SsoSecurityService } from '../../login/services/sso-security.service';
import { RoutingService } from '../../services/routing.service';
import { AlertsComponent } from '../../alerts/alerts.component';
import { FeatureService } from '../../services/feature.service';
import { AboutComponent } from '../../components/about/about.component';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Keepalive } from '@ng-idle/keepalive';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';

import { UITheme } from '../../theme/theme.interface';

/**
 * App home component.
 */
@Component({
	selector: 'navigation',
	templateUrl: './navigation.component.html',
	styleUrl: './navigation.component.css'
})
export class NavigationComponent implements OnInit, OnDestroy {
	loginModalInstance: NgbModalRef | undefined = undefined;
	modalInstance: NgbModalRef | undefined = undefined;
	
	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};
	permissions: { [key: string]: any } = {};
	user: any = {};
	
	profileOn: boolean = false;
	cartOn: boolean = false;
	downloadOn: boolean = false;
	passwordEnabled: boolean = true;
	userPasswordDisabled: boolean = false;
	passwordExpired: boolean = false;
	admin: boolean = false;
	upload: boolean = false;
	globalRecords: any = {
		all: false
	};
	alerts: any[] = [];
	alertsMode: string = "";
	alertsView: any[] = [];
	messagesCount: number = 0;
	messages: any[] = [];
	ocr: any = { available: false };
	socials: any = {};
	postLoginRoute: string = '';
	cartCount: number = 0;
	theme: any = {};
	isUserAllowedToSearch: boolean = true;
	dxOn: boolean = false;
	registerEnabled: boolean = false;
	authenticationUIDisabled: boolean = false;
	albumOn: boolean = false;
	orderOn: boolean = false;
	messagesOpen: boolean = false;
	records: any[] = [];
	states: any = {};
	showDisclaimer: boolean = false;
	isMobile: boolean = false;
	
	themes: UITheme[] = [];

	private userServiceSubscription: Subscription = new Subscription();
	private alertServiceSubscription: Subscription = new Subscription();
	private alertDeleteEventSubscription: Subscription = new Subscription();
	private cartEventSubscription: Subscription = new Subscription();
	private searchServiceSubscription: Subscription = new Subscription();
	private idleServiceSubscription: Subscription = new Subscription();
	private messagesServiceSubscription: Subscription = new Subscription();
	private keepaliveSubscription!: Subscription;
	private themeEventSubscription!: Subscription;
	private configSubscription!: Subscription;

	constructor(
		private viewportService: ViewportService,
		private configService: ConfigService,
		private userService: UserService,
		private alertsService: AlertsService,
		private searchService: SearchService,
		private cartService: CartService,
		private messagesService: MessagesService,
		private themeBuilderService: ThemeBuilderService,
		private featureService: FeatureService,
		private ssoService: SsoSecurityService,
		private idleService: IdleService,
		private routingService: RoutingService,
		private http: HttpClient,
		private location: Location,
		private modalService: NgbModal,
		private router: Router,
		private keepalive: Keepalive,
		private sanitizer: DomSanitizer
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		
		this.viewportService.isMobile$.subscribe(isMobile => {
			this.isMobile = isMobile;
		});
	}

	ngOnInit(): void {
		this.userPermissions = this.userService.getUserPermissionsMap();
		this.user = this.userService.user;
		this.alerts = this.alertsService.getAlerts(this.user?.username);
		this.alertsView = this.alerts;
		this.cartCount = this.cartService.count;
		
		const params = { ...this.routingService.getQueryParams() };
		const mapDetached: boolean = (params['mapDetach']) ? this.sanitize(params['mapDetach']) == "true" : false;
		const detachedParam = params['detached'];
		const queryParam = params['q'];
		const detached = detachedParam ? this.sanitize(detachedParam) === 'true' : false;
		const query = queryParam ? this.sanitize(queryParam) : '';
		this.states = {
			detached: detached,
			advancedOpen: !(detached || mapDetached),
			browseOpen: '',
			query: query,
			mapDetach: mapDetached
		};
		
		this.loadThemes();
		this.initConfigProperties();
		this.initNav();
		
		// When this component initializes, the config service is not guaranteed to have yet loaded the
		// full config from the server, only the default app json config from assets.
		this.configSubscription = this.configService.configsLoadedEvent.subscribe(() => {
			this.initConfigProperties();
			this.initNav();
		});
		
		this.userServiceSubscription = this.userService.broadcastEvent.subscribe((eventId: string) => {
			switch (eventId) {
				case 'localizationLoaded':
					this.LOCALIZATION = this.userService.LOCALIZATION;
					break;
				case 'userPrefsUpdate':
					this.user = this.userService.user;
					this.userPasswordDisabled = !!this.user?.passwordDisabled;
					this.initNav();
					break;
				case 'register':
					this.showLogin("register");
					break;
				case 'login':
					this.showLogin("login");
					break;
				case 'loggedin':
					this.loadThemes();
					break;	
				case 'dismissLoginModal':
					if (this.loginModalInstance) {
						this.loginModalInstance.dismiss('cancel');
						this.loginModalInstance = undefined;
					}
					this.closeTopModalIfOpen()
					break;
				case 'disclaimerAccepted':
					this.showDisclaimer = false;
					break;	
			}
		});

		this.alertServiceSubscription = this.alertsService.addAlertEvent.subscribe((args: { alert: any; timestamp: Date }) => {
			args = (args) || ({});
			if (this.alertsMode == "") {
				this.alertsView = this.alerts;
			}
			else if (this.alertsMode == "info") {
				this.alertsView = this.alerts.filter((alert: any) => { return alert.status == "info" });
			}
			else if (this.alertsMode == "success") {
				this.alertsView = this.alerts.filter((alert: any) => { return alert.status == "success" });
			}
			else {
				this.alertsView = this.alerts.filter((alert: any) => { return alert.status == "danger" });
			}
		});
		
		this.alertDeleteEventSubscription = this.alertsService.notifyDeleteAlertEvent.subscribe((args: { timestamp: Date }) => {
			this.alerts = this.alertsService.getAlerts(this.userService.user?.username || '');
			if (this.alertsMode == "") {
				this.alertsView = this.alerts;
			}
			else if (this.alertsMode == "info") {
				this.alertsView = this.alerts.filter((alert: any) => { return alert.status == "info" });
			}
			else if (this.alertsMode == "success") {
				this.alertsView = this.alerts.filter((alert: any) => { return alert.status == "success" });
			}
			else {
				this.alertsView = this.alerts.filter((alert: any) => { return alert.status == "danger" });
			}
		});

		this.cartEventSubscription = this.cartService.broadcastEvent.subscribe((eventId: string) => {
			switch (eventId) {
				case 'cartUpdated':
					this.cartCount = this.cartService.count;
					break;
			}
		});
		
		this.searchServiceSubscription = this.searchService.broadcastEvent.subscribe((args: any) => {
			switch (args.type) {
				case 'searchEnded':
					this.records = args.data.records || [];
					break;
				case 'cleanSearches':
					this.records = [];
					this.searchService.initializeSvc();
					this.searchService.broadcastClearSearchEvent();
					break;
				case 'closeModal':
					if (this.modalInstance) {
						this.modalInstance.dismiss("cancel");
						this.modalInstance = undefined;
					}
					break;
			}
		});
		
		this.idleServiceSubscription = this.idleService.idleLogoutEvent.subscribe((args: {
			eventType: string, forced: boolean, timestamp: Date
		}) => {
			switch (args.eventType) {
				case 'idleLogout':
					this.logout(args.forced);
					break;
			}
		});
		
		this.messagesServiceSubscription = this.messagesService.broadcastEvent.subscribe((eventId: string) => {
			switch (eventId) {
				case 'reloadMessages':
					this.reloadMessages();
					break;
			}
		});

		this.keepaliveSubscription = this.keepalive.onPing.subscribe(() => {
			// FIXME: why is this in navigation component?
			console.log('Keepalive event received');
			// do something to keep the user's session alive;
			if (this.configService.getConfig().auth?.public?.autoEnabled || this.userService.user.loggedin) {
				this.userService.whenGetUserdata().then(() => {
					if (this.userService.user.loggedin) {
						this.reloadMessages();
						this.messagesService.triggerMessagesReload();
					}
				}).catch((error: any) => {
					if (this.configService.getConfig().auth?.public?.autoEnabled) {
						this.userService.attemptPublicUserLogin().then(() => {
							this.userService.processToken().then(() => {
								this.goHome();
							});
						}).catch((error: any) => {
							const config = this.configService.getConfig();
							if (config.auth && config.auth?.public) {
								config.auth.public.autoEnabled = false;
							}
							this.onLogout();
						});
					}
				});
			}
		});
		
		this.themeEventSubscription = this.themeBuilderService.themeEvent.subscribe((event: any) => {
			switch (event.eventType) {
				case 'themeCreated':
					if (event?.data?.theme?.enabled) {
						this.themes.push(event.data.theme);
					} 
					break;
				case 'themeUpdated':
					this.loadThemes();
					break;
			}
		});	
	}

	ngOnDestroy() {
		if (this.userServiceSubscription) {
			this.userServiceSubscription.unsubscribe();
		}
		if (this.alertServiceSubscription) {
			this.alertServiceSubscription.unsubscribe();
		}
		if (this.alertDeleteEventSubscription) {
			this.alertDeleteEventSubscription.unsubscribe();
		}
		if (this.cartEventSubscription) {
			this.cartEventSubscription.unsubscribe();
		}
		if (this.searchServiceSubscription) {
			this.searchServiceSubscription.unsubscribe();
		}
		if (this.idleServiceSubscription) {
			this.idleServiceSubscription.unsubscribe();
		}
		if (this.messagesServiceSubscription) {
			this.messagesServiceSubscription.unsubscribe();
		}
		if (this.keepaliveSubscription) {
			this.keepaliveSubscription.unsubscribe();
		}
		if (this.themeEventSubscription) {
			this.themeEventSubscription.unsubscribe();
		}
		if (this.configSubscription) {
			this.configSubscription.unsubscribe();
		}
	}
	
	private initConfigProperties() {
		this.socials = {
			facebook: this.configService.getConfig().ui?.social?.facebook.enabled ? this.configService.getConfig().ui.social.facebook.link : "",
			instagram: this.configService.getConfig().ui?.social?.instagram.enabled ? this.configService.getConfig().ui.social.instagram.link : "",
			x: this.configService.getConfig().ui?.social?.x.enabled ? this.configService.getConfig().ui.social.x.link : "",
			linkedin: this.configService.getConfig().ui?.social?.linkedIn.enabled ? this.configService.getConfig().ui.social.linkedIn.link : "",
			youtube: this.configService.getConfig().ui?.social?.youtube.enabled ? this.configService.getConfig().ui.social.youtube.link : ""
		};
		
		this.postLoginRoute = this.configService.getConfig().auth?.logoutSplash ? "logout-splash" : "login";
		
		if (this.configService.getConfig()?.auth?.disclaimer) {
			let disclaimerAccepted = localStorage.getItem('kv_disclaimer') === 'accepted';
			if (!disclaimerAccepted) {
				this.showDisclaimer = true;
			}
		}		
	}

	onLogout() {
		if (this.postLoginRoute === "logout-splash") {
			this.goToMode(this.postLoginRoute);
		} else {
			this.showLogin(this.postLoginRoute);
		}
	}

	closeLiveAlerts(): void {
		const elements = document.getElementsByClassName('ui-notification') as HTMLCollectionOf<HTMLElement>;
		Array.from(elements).forEach(el => {
			el.style.visibility = 'hidden';
		});
	}

	toggleAlertMode(mode: string): void {
		this.alertsMode = this.alertsMode === mode ? '' : mode;
		switch (this.alertsMode) {
			case '':
				this.alertsView = this.alerts;
				break;
			case 'info':
			case 'success':
			case 'danger':
				this.alertsView = this.alerts.filter(alert => alert.status === this.alertsMode);
				break;
			default:
				this.alertsView = this.alerts;
				break;
		}
	}

	showAlert(index: number): void {
		let alert = this.alerts[index];
		let show = alert.show;
		alert.show = !show;
		this.alerts[index] = alert;
		sessionStorage.setItem('alerts', JSON.stringify(this.alerts));
	}

	showAllAlerts() {
		this.searchService.broadcastEvent.emit({ type: 'closeModal', data: {} });
		this.modalInstance = this.modalService.open(AlertsComponent, {
			size: 'md',
			backdrop: false,
			centered: true
		});
	}

	showLogin(mode: string): void {
		if (this.loginModalInstance) {
			return;
		}
		
		/** TODO revisit after full migration**/
		//this.router.navigate([`/${mode}`]);
		this.router.navigate(['/login'],{ 'queryParams': { 'm': mode }});
	}

	closeAlert(index: number) {
		this.alerts = this.alertsService.removeAlert(index);
		switch (this.alertsMode) {
			case '':
				this.alertsView = this.alerts;
				break;
			case 'info':
			case 'success':
				this.alertsView = this.alerts.filter(alert => alert.status === this.alertsMode);
				break;
			default:
				this.alertsView = this.alerts.filter(alert => alert.status === "danger");
				break;
		}
	}

	clearAlerts() {
		this.alerts = [];
		this.alertsView = [];
		this.alertsService.removeAllAlerts();
	}

	themeSupported(): boolean {
		// Detect if this browser is not Internet Explorer
		const ua = window.navigator.userAgent;
		return !/MSIE|Trident/.test(ua);
	}
	
	async loadThemes(): Promise<any> {
		this.themes = await this.themeBuilderService.getEnabledThemes();
	}

	setTheme(theme: any, local: boolean = false): void {
		this.themeBuilderService.setThemeAttribute(theme);

		const tmp = this.userService.getLocalStorageData();
		tmp.theme = theme;
		this.userService.setLocalStorageData(tmp);

		if (!local) {
			this.userService.updatePartialUser({ theme });
		}
	}

	logout(forced: boolean = false): void {
		this.setTheme('', true);
		this.alertsService.removeAllAlerts();

		const publicConfig = this.configService.getConfig().auth.public;

		if (publicConfig.autoEnabled) {
			this.userService.logout().then(() => {
				this.userService.attemptPublicUserLogin().then(() => {
					this.userService.processToken().then(() => {
						if (forced) {
							this.alertsService.error({
								title: this.LOCALIZATION.sessionTimeout,
								message: this.LOCALIZATION.sessionTimeoutMessage,
								delay: 5000
							});
							this.goHome();
							// Idle.watch(); // If you're using Idle tracking in Angular
						}
					});
				}).catch(() => {
					this.configService.getConfig().auth.public.autoEnabled = false;
					this.onLogout();
				});
			}).catch(() => { });
		} else {
			let isSplashPage = this.postLoginRoute === "logout-splash";
			this.userService.logout(false, !isSplashPage).then(() => {
				this.user = this.userService.user;
				this.onLogout();
			}).catch(() => { });
		}
	}

	displayTipsheet(): void {
		let url = '';

		if (this.permissions['ROLE_PRODUCT_DOWNLOAD'] && this.permissions['ROLE_PRODUCT_VIEW']) {
			// county and admin
			url = '../../app/misc/srs_tip_sheet.pdf';
		} else if (
			this.permissions['ROLE_PRODUCT_VIEW'] &&
			this.permissions['ROLE_PRODUCT_DOWNLOAD_RESTRICTED']
		) {
			// kiosk user
			url = '../../app/misc/srs_tip_sheet_default.pdf';
		} else {
			// fallback on public user
			url = '../../app/misc/srs_tip_sheet_default.pdf';
		}

		const width = window.innerWidth / 2;
		const height = window.innerHeight;
		window.open(
			url,
			'',
			`height=${height},width=${width},resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,directories=no,status=no`
		);
	}

	displayHelp(): void {
		const helpCustomTemplates =
			this.configService.getConfig()?.ui?.customPages?.help || [];

		const group = this.userService.user?.group;
		const customTemplate = helpCustomTemplates.find(
			(templateItem: any) => templateItem.group === group
		)?.template;

		const url = customTemplate || '../../app/load/help/en/help.html';
		window.open(url, '_blank');
	}

	adminSite(): void {
		window.location.href = `${window.location.origin}/aw-admin-ui/`;
	}

	about() {
		this.searchService.broadcastEvent.emit({ type: 'closeModal', data: {} });
		this.modalInstance = this.modalService.open(AboutComponent, {
			size: 'sm',
			backdrop: false,
			centered: true
		});
	}

	initNav(): void {
		const config = this.configService.getConfig();
		const user = this.userService.user;
		
//		console.log(`userAuth: ${user.username}`);
//		console.log(`userAuth loggedin: ${user.loggedin}`);
//		console.log(`userAuth autologin: ${user.autologin}`);

		this.isUserAllowedToSearch = config?.auth?.public?.autoEnabled || user?.loggedin;

		const permissionsArray = user?.permissions || [];
		const permissions = permissionsArray.reduce((map: Record<string, number>, key: string) => {
			map[key] = 1;
			return map;
		}, {});
		this.userPermissions = permissions;

		this.downloadOn = !!permissions['ROLE_PRODUCT_DOWNLOAD']
			? false
			: !!permissions['ROLE_TRANSACTION_HISTORY'];

		this.cartOn = !!permissions['ROLE_PRODUCT_DOWNLOAD_RESTRICTED'] && !permissions['ROLE_PRODUCT_DOWNLOAD'];

		this.profileOn = !!permissions['ROLE_USER_PROFILE'];

		this.admin = !!permissions['ROLE_USERS_EDIT'] || !!permissions['ROLE_SYSTEM_SUMMARY_VIEW'];

		this.upload = !!permissions['ROLE_PRODUCT_ADD'];

		this.albumOn = !!permissions['ROLE_ALBUM_MANAGEMENT'] || !!permissions['ROLE_ALBUM_VIEW'];

		this.orderOn =
			!!permissions['ROLE_ORDER_MANAGEMENT'] ||
			!!permissions['ROLE_ORDER_VIEW'] ||
			!!permissions['ROLE_PRODUCT_DOWNLOAD'];

		if (permissions['ROLE_NOTIFICATION_MANAGEMENT'] || permissions['ROLE_NOTIFICATION_SUBSCRIBE']) {
			this.messagesService.getUserMessagesUnreadCount().then((count: number) => {
				this.messagesCount = count;
			}).catch((error: any) => {
				console.error('Error fetching messages count:', error);
			});
		}

		this.dxOn = config.dx?.enabled;
		this.userPasswordDisabled = !!user?.passwordDisabled;
		this.isPasswordEnabled();
		this.registerEnabled = config.auth?.register || config.auth?.public?.register;
		this.authenticationUIDisabled = config.auth?.public?.hideAuthenticationUI && (user.autologin || !user.loggedin);
		this.userService.isFacadeEnabled('OCRFacade').then((available: boolean) => {
			this.ocr.available = available;
		}).catch((error: any) => {
			this.ocr.available = false;
		});
	}

	isPasswordEnabled(): void {
		this.ssoService.getPasswordLoginEnabled().then(
			(response: boolean) => {
				this.passwordEnabled = response;
			}).catch((error: any) => {
				console.error("Failed to get password login enabled status", error);
			});
	}

	reloadMessages(): void {
		//use this do anykind of updates on global entities like message count;
		this.messagesService.getUserMessagesUnreadCount().then((count: number) => {
			if (count > this.messagesCount) {
				//assume there are new messages;
				this.alertsService.info({
					title: "New Message",
					message: this.LOCALIZATION.messageNew,
					delay: 3000
				});
			}
			this.messagesCount = count;
			if (this.messagesOpen) {
				this.loadMessages();
			};
		}).catch((error: any) => {
			console.info("Failed to reload messages");
		});
	}

	displayMessages() {
		this.messagesOpen = !this.messagesOpen;
		if (this.messagesOpen) {
			this.loadMessages();
		}
	}

	openMessage(index: number) {
		let message: any = this.messages[index];
		if (message) {
			message.open = !message.open;
			//mark as read;
			message.read = true;
			this.messagesService.readUserMessage(message.id).then(() => {
				this.messagesService.getUserMessagesUnreadCount().then((count: number) => {
					this.messagesCount = count;
				}).catch((error: any) => {
					console.error("Failed to getUserMessagesUnreadCount", error);
				});
			}).catch((error: any) => {
				console.error("Failed to read user message", error);
			});
		}
	}

	goHome() {
		this.userService.goHome();
	}

	closeDropdown(dropdown: NgbDropdown) {
		if (dropdown && dropdown.isOpen()) {
			dropdown.close(); // closes the dropdown
		}
	}
	
	goToMode(mode: string, queryString: string = '') {
	  const queryParams: Record<string, string> = this.parseQueryString(queryString);
	  // Navigate to the constructed URL
	   this.router.navigate([`/${mode}`], { queryParams }).then(() => {
	     if (mode === 'profile') {
	      this.userService.runBroadcastEvent("openProfileMessages");
	     }
	   });
	}
	
	openThemeBuilder(): void {
		this.themeBuilderService.open();
	}
	
	private parseQueryString(queryString: string): Record<string, string> {
	  const params: Record<string, string> = {};
	  if(queryString){
		  if (queryString.startsWith('?')) {
		    queryString = queryString.substring(1);
		  }
		  const pairs = queryString.split('&');
		  for (const pair of pairs) {
		    const [key, value] = pair.split('=');
		    if (key) {
		      params[decodeURIComponent(key)] = decodeURIComponent(value || '');
		    }
		  }
	  }
	  return params;
	}
    
	loadMessages(): void {
		this.messagesService.getUserUnreadMessages(0, 5).then((response: any) => {
			this.messages = response.content.map((msg: any) => {
				const created = new Date(msg.created);
				msg.created = created.toLocaleDateString('en-US') + ' ' + created.toLocaleTimeString('en-US');

				try {
					msg.messageObj = JSON.parse(msg.message);
				} catch (err) {
					// parsing failed
				}

				return msg;
			});

			const expressions = [
				/".+"\sfrom\sSaved\sSearches/,
				/"My\sSubscriptions"\sarea\s.+To examine/,
				/Album\sname:\s.+[.]{1}\sFrom/
			];

			this.messages.forEach((message: any, i: number) => {
				const content = message.messageObj;
				const msg = content?.message ?? '';

				for (let j = 0; j < expressions.length; j++) {
					const match = msg.match(expressions[j]);
					if (match && match.length > 0) {
						let word = '';
						let constraints: any = {};
						const matchedText = match[0];

						switch (j) {
							case 0: // Saved Searches
								word = matchedText.substring(1, matchedText.indexOf(' from Saved Searches') - 1);
								constraints = { name: word };
								this.searchService.getSubscribedSearches(constraints).then((res: any) => {
									const subscription = res.content?.[0];
									message.arg = subscription;
									message.action = (searchObj: any) => {
										this.searchService.broadcastEvent.emit({
											type: 'userTriggeredSearchExecution', data: {
												searchObj: searchObj,
												notify: true
											}
										});
									};
								});
								break;

							case 1: // My Subscriptions
								word = matchedText.substring(24, matchedText.indexOf('. To examine'));
								constraints = { name: word };
								this.searchService.getSubscribedSearches(constraints).then((res: any) => {
									const subscription = res.content?.[0];
									message.arg = subscription;
									message.action = (searchObj: any) => {
										this.searchService.broadcastEvent.emit({
											type: 'userTriggeredSearchExecution', data: {
												searchObj: searchObj,
												notify: true
											}
										});
									};
								});
								break;

							case 2: // Album name (commented out in original)
								word = matchedText.substring(12, matchedText.indexOf('. From'));
								constraints = { name: word };
								// You can re-add this block if needed
								/**
								const pagination = {
								  elementsPerPage: 12,
								  page: 1,
								  sorting: {
									sort: "created,desc"
								  }
								};
						  	
								try {
								  const res = await this.albumsService.getAlbums(0, pagination, constraints);
								  let albums = res.content;
								  let album = albums.find(a => a.name === constraints.name) || albums[0];
						  	
								  this.messages[index].arg = album;
								  this.messages[index].action = (album: any) => {
									if (album.pages >= 1) {
									  this.docOpenerService.open(album.id, null, album);
									}
								  };
								} catch (err) {
								  console.error("Error loading album messages", err);
								}
								 */
								break;
						}
					}
				}
			});
		});
	}

	closeTopModalIfOpen(): void {
		if (this.modalService.hasOpenModals()) {
			this.modalService.dismissAll('cancel');
		}
	}
	
	sanitize(value: string): string {
		return this.sanitizer.sanitize(1, value) || ''; // 1 = SecurityContext.HTML
	}
	
	isFeatureEnabled(feature: string): boolean {
		return this.featureService.isFeatureEnabled(feature);
	}
	
	getBrandURL(): string {
		let url = this.featureService.getFeatureValue('BrandUrl');
		if (!url) {
			url = "";
		}
		return url;
	}  
		
	openUrl(url: string): void {
		window.open(url, '_blank');
	}
}
